import Modal from '@components/Modal'
import CurrencySelect from '@components/CurrencySelect'
import { getCurrencyByCode } from '@/constants/currencies'
import { useAuth } from '@context/AuthContext'
import { useImageUpload } from '@hooks/useImageUpload'
import {
    getInitials,
    getMemberName,
    getParticipantsPayload,
    getSplitValidation,
    sortMembersWithCurrentFirst,
    SplitType,
    toCents,
} from '../expenseForm'
import { GetUserType } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import {
    AmountInput,
    AmountInputWrapper,
    AmountPrefix,
    Button,
    ButtonGroup,
    FormDescription,
    FormGroup,
    FormInput,
    FormLabel,
    FormSelect,
    HiddenInput,
    ImageActionButton,
    ImageActions,
    ImageOverlay,
    ImagePlaceholder,
    ImagePreview,
    ImageSection,
    ParticipantCheckbox,
    ParticipantName,
    ParticipantRow,
    ParticipantSplitInput,
    ParticipantsList,
    ProfileAvatar,
    SplitError,
    SplitSummary,
    SplitTypeButton,
    SplitTypeContainer,
} from './AddExpenseModal.styles'

interface AddExpenseModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: {
        name: string
        amount: number
        paidByUserId: number
        splitType: SplitType
        currencyCode: string
        participants: { userId: number; splitAmount: number | null }[]
        imageFile?: File
    }) => void
    members: GetUserType[]
    defaultCurrencyCode: string
    isSubmitting?: boolean
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    members,
    defaultCurrencyCode,
    isSubmitting = false,
}) => {
    const { user } = useAuth()

    const [name, setName] = useState('')
    const [amount, setAmount] = useState('')
    const [currencyCode, setCurrencyCode] = useState(defaultCurrencyCode)
    const [paidByUserId, setPaidByUserId] = useState<number>(user?.id ?? 0)
    const [splitType, setSplitType] = useState<SplitType>('Equally')
    const [selectedParticipants, setSelectedParticipants] = useState<Set<number>>(
        new Set(members.map(m => m.id))
    )
    const [splitAmounts, setSplitAmounts] = useState<Record<number, string>>({})
    const {
        imageFile,
        previewUrl: imagePreviewUrl,
        showOverlay: showImageOverlay,
        setShowOverlay: setShowImageOverlay,
        fileInputRef,
        handleImageSelect,
        handleImageRemove,
    } = useImageUpload({ isOpen })

    useEffect(() => {
        if (isOpen) {
            setName('')
            setAmount('')
            setCurrencyCode(defaultCurrencyCode)
            setPaidByUserId(user?.id ?? 0)
            setSplitType('Equally')
            setSelectedParticipants(new Set(members.map(m => m.id)))
            setSplitAmounts({})
        }
    }, [isOpen, members, defaultCurrencyCode, user?.id])

    const amountCents = toCents(amount)
    const currencyInfo = getCurrencyByCode(currencyCode)
    const symbol = currencyInfo?.symbol ?? '$'

    const toggleParticipant = (userId: number) => {
        setSelectedParticipants(prev => {
            const next = new Set(prev)
            if (next.has(userId)) {
                next.delete(userId)
            } else {
                next.add(userId)
            }
            return next
        })
    }

    const handleSplitAmountChange = (userId: number, value: string) => {
        setSplitAmounts(prev => ({ ...prev, [userId]: value }))
    }

    const validation = getSplitValidation(selectedParticipants, splitType, splitAmounts, amount, symbol)

    const canSubmit =
        name.trim().length > 0 &&
        parseFloat(amount || '0') > 0 &&
        validation.valid &&
        !isSubmitting

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return
        onSubmit({
            name: name.trim(),
            amount: amountCents,
            paidByUserId,
            splitType,
            currencyCode,
            participants: getParticipantsPayload(selectedParticipants, splitType, splitAmounts),
            imageFile: imageFile ?? undefined,
        })
    }

    // Sort members with current user first
    const sortedMembers = sortMembersWithCurrentFirst(members, user?.id)

    const formId = 'add-expense-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Add Expense"
            size="large"
            backgroundColor="#fff8f6"
            closeButtonColor="#a03c04"
            footer={
                <ButtonGroup>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" form={formId} variant="primary" disabled={!canSubmit}>
                        {isSubmitting ? 'Adding...' : 'Add Expense'}
                    </Button>
                </ButtonGroup>
            }
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="expense-name">Expense Name</FormLabel>
                    <FormInput
                        id="expense-name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g., Dinner at the restaurant"
                        maxLength={75}
                        autoFocus
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Image</FormLabel>
                    <FormDescription>
                        Attach a photo of a receipt, bill, or anything related to the expense
                    </FormDescription>
                    <HiddenInput
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                    />
                    <ImageSection>
                        {imagePreviewUrl ? (
                            <>
                                <ImagePreview onClick={() => setShowImageOverlay(true)}>
                                    <img src={imagePreviewUrl} alt="Receipt" />
                                </ImagePreview>
                                <ImageActions>
                                    <ImageActionButton
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Replace Image
                                    </ImageActionButton>
                                    <ImageActionButton
                                        type="button"
                                        $danger
                                        onClick={handleImageRemove}
                                    >
                                        Delete Image
                                    </ImageActionButton>
                                </ImageActions>
                            </>
                        ) : (
                            <ImagePlaceholder onClick={() => fileInputRef.current?.click()}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                </svg>
                            </ImagePlaceholder>
                        )}
                    </ImageSection>
                </FormGroup>

                {showImageOverlay && imagePreviewUrl && (
                    <ImageOverlay onClick={() => setShowImageOverlay(false)}>
                        <img src={imagePreviewUrl} alt="Receipt full size" />
                    </ImageOverlay>
                )}

                <FormGroup>
                    <FormLabel htmlFor="expense-amount">Amount</FormLabel>
                    <AmountInputWrapper>
                        <AmountPrefix>{symbol}</AmountPrefix>
                        <AmountInput
                            id="expense-amount"
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="0.00"
                            min="0.01"
                            step="0.01"
                        />
                    </AmountInputWrapper>
                </FormGroup>

                <FormGroup>
                    <CurrencySelect
                        value={currencyCode}
                        onChange={setCurrencyCode}
                        label="Currency"
                        htmlFor="expense-currency"
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="expense-paid-by">Paid By</FormLabel>
                    <FormSelect
                        id="expense-paid-by"
                        aria-label="Paid By"
                        value={paidByUserId}
                        onChange={e => setPaidByUserId(Number(e.target.value))}
                    >
                        {sortedMembers.map(member => (
                            <option key={member.id} value={member.id}>
                                {getMemberName(member, user?.id)}
                            </option>
                        ))}
                    </FormSelect>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Split Type</FormLabel>
                    <SplitTypeContainer>
                        {(['Equally', 'Percent', 'Value'] as SplitType[]).map(type => (
                            <SplitTypeButton
                                key={type}
                                type="button"
                                $active={splitType === type}
                                onClick={() => {
                                    setSplitType(type)
                                    setSplitAmounts({})
                                }}
                            >
                                {type === 'Equally' ? 'Equal' : type}
                            </SplitTypeButton>
                        ))}
                    </SplitTypeContainer>
                </FormGroup>

                <FormGroup>
                    <FormLabel>Split Between</FormLabel>
                    <ParticipantsList>
                        {sortedMembers.map(member => {
                            const isSelected = selectedParticipants.has(member.id)
                            return (
                                <ParticipantRow
                                    key={member.id}
                                    $isSelected={isSelected}
                                    onClick={() => toggleParticipant(member.id)}
                                >
                                    <ParticipantCheckbox $checked={isSelected} />
                                    <ProfileAvatar>
                                        {member.profilePicture ? (
                                            <img src={member.profilePicture} alt={getMemberName(member, user?.id)} />
                                        ) : (
                                            getInitials(member)
                                        )}
                                    </ProfileAvatar>
                                    <ParticipantName>{getMemberName(member, user?.id)}</ParticipantName>
                                    {isSelected && splitType !== 'Equally' && (
                                        <ParticipantSplitInput
                                            type="number"
                                            placeholder={splitType === 'Percent' ? '%' : '0.00'}
                                            value={splitAmounts[member.id] || ''}
                                            onChange={e => handleSplitAmountChange(member.id, e.target.value)}
                                            onClick={e => e.stopPropagation()}
                                            min="0"
                                            step={splitType === 'Percent' ? '1' : '0.01'}
                                        />
                                    )}
                                </ParticipantRow>
                            )
                        })}
                    </ParticipantsList>
                    <SplitSummary>
                        {validation.valid ? validation.message : <SplitError>{validation.message}</SplitError>}
                    </SplitSummary>
                </FormGroup>
            </form>
        </Modal>
    )
}

export default AddExpenseModal
